// src/app/services/credenciais-service/secure-credentials.service.ts
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

@Injectable({
  providedIn: 'root'
})
export class SecureCredentialsService {
  private isApp: boolean;
  private useSecureStorage: boolean = true;

  constructor() {
    const platform = Capacitor.getPlatform();
    this.isApp = platform === 'android' || platform === 'ios';
    
    // 🔥 Em Android, o SecureStorage pode não funcionar em algumas versões
    // Vamos testar se funciona, se não, usa Preferences
    this.useSecureStorage = this.isApp;
    
    console.log('📱 SecureCredentialsService - Platform:', platform);
    console.log('📱 SecureCredentialsService - isApp:', this.isApp);
    console.log('📱 SecureCredentialsService - useSecureStorage:', this.useSecureStorage);
  }

  async saveCredentials(identificador: string, senha: string): Promise<boolean> {
    try {
      if (!this.isApp) {
        console.warn('⚠️ Web: não salvar credenciais por segurança');
        return false;
      }

      console.log('💾 Salvando credenciais...');
      
      // 🔥 Tenta usar SecureStorage primeiro
      if (this.useSecureStorage) {
        try {
          await SecureStoragePlugin.set({
            key: 'usuario_identificador',
            value: identificador
          });

          await SecureStoragePlugin.set({
            key: 'usuario_senha',
            value: senha
          });
          
          console.log('✅ Credenciais salvas no SecureStorage');
          return true;
        } catch (error) {
          console.warn('⚠️ SecureStorage falhou, usando Preferences como fallback:', error);
          this.useSecureStorage = false;
        }
      }

      // 🔥 Fallback: Usa Preferences
      await Preferences.set({
        key: 'usuario_identificador',
        value: identificador
      });

      await Preferences.set({
        key: 'usuario_senha',
        value: senha
      });

      console.log('✅ Credenciais salvas no Preferences (fallback)');
      return true;
    } catch (error) {
      console.error('❌ Erro ao salvar credenciais:', error);
      return false;
    }
  }

  async getCredentials(): Promise<{ identificador: string; senha: string } | null> {
    try {
      if (!this.isApp) {
        console.warn('⚠️ Web: não recuperar credenciais');
        return null;
      }

      console.log('🔍 Recuperando credenciais...');
      
      let identificador: string | null = null;
      let senha: string | null = null;

      // 🔥 Tenta SecureStorage primeiro
      if (this.useSecureStorage) {
        try {
          const idResult = await SecureStoragePlugin.get({ key: 'usuario_identificador' });
          const senhaResult = await SecureStoragePlugin.get({ key: 'usuario_senha' });
          
          identificador = idResult.value;
          senha = senhaResult.value;
          
          if (identificador && senha) {
            console.log('✅ Credenciais recuperadas do SecureStorage');
            return { identificador, senha };
          }
        } catch (error) {
          console.warn('⚠️ SecureStorage falhou, tentando Preferences:', error);
          this.useSecureStorage = false;
        }
      }

      // 🔥 Fallback: Preferences
      const idResult = await Preferences.get({ key: 'usuario_identificador' });
      const senhaResult = await Preferences.get({ key: 'usuario_senha' });
      
      identificador = idResult.value || null;
      senha = senhaResult.value || null;

      if (identificador && senha) {
        console.log('✅ Credenciais recuperadas do Preferences (fallback)');
        return { identificador, senha };
      }

      console.log('ℹ️ Nenhuma credencial encontrada');
      return null;
    } catch (error) {
      console.log('ℹ️ Nenhuma credencial encontrada:', error);
      return null;
    }
  }

  async hasCredentials(): Promise<boolean> {
    try {
      if (!this.isApp) return false;
      
      // Tenta SecureStorage
      if (this.useSecureStorage) {
        try {
          const result = await SecureStoragePlugin.get({ key: 'usuario_identificador' });
          if (result.value) return true;
        } catch {
          this.useSecureStorage = false;
        }
      }
      
      // Fallback Preferences
      const result = await Preferences.get({ key: 'usuario_identificador' });
      return !!result.value;
    } catch {
      return false;
    }
  }

  async clearCredentials(): Promise<boolean> {
    try {
      if (!this.isApp) {
        console.warn('⚠️ Web: não há credenciais para remover');
        return false;
      }

      console.log('🗑️ Removendo credenciais...');

      // Tenta SecureStorage
      if (this.useSecureStorage) {
        try {
          await SecureStoragePlugin.remove({ key: 'usuario_identificador' });
          await SecureStoragePlugin.remove({ key: 'usuario_senha' });
          console.log('✅ Credenciais removidas do SecureStorage');
        } catch (error) {
          console.warn('⚠️ SecureStorage falhou ao remover:', error);
          this.useSecureStorage = false;
        }
      }

      // Sempre tenta remover do Preferences também (por segurança)
      await Preferences.remove({ key: 'usuario_identificador' });
      await Preferences.remove({ key: 'usuario_senha' });

      console.log('✅ Credenciais removidas com sucesso');
      return true;
    } catch (error) {
      console.error('❌ Erro ao remover credenciais:', error);
      return false;
    }
  }
}